/**
 * Centralized Analytics Utility Module
 * Google Analytics 4 event tracking for Astroloo
 */

const PRIVATE_RESULT_PATH = /^\/(mapa|horoscopo)\/[^/]+(?:\/[^/]+)*\/?$/;
const BIRTHCHART_ANALYTICS_URL = '/mapa';
const BIRTHCHART_ANALYTICS_TITLE = 'Mapa Astral - Astralmapa';
const HOROSCOPE_ANALYTICS_URL = '/horoscopo';
const HOROSCOPE_ANALYTICS_TITLE = 'Horóscopo - Astralmapa';
const PRIVATE_ANALYTICS_PARAMETER_KEYS = new Set([
    'birthcharthash',
    'chartid',
    'charthash',
    'horoscopehash',
    'pagereferrer',
    'referrer',
    'reporthash',
    'urlparams'
]);
const RESERVED_ACQUISITION_PARAMETER_KEYS = new Set([
    'campaign',
    'campaign_content',
    'campaign_id',
    'campaign_medium',
    'campaign_name',
    'campaign_source',
    'campaign_term',
    'medium',
    'source',
    'utm_campaign',
    'utm_content',
    'utm_id',
    'utm_medium',
    'utm_source',
    'utm_term'
]);
const SUBSCRIPTION_SOURCE_ALLOWLIST = new Set([
    'birthchart_pdf',
    'birthchart_ai_chat',
    'cancel_page_subscribe',
    'cancel_page_try_again',
    'compatibility_history',
    'compatibility_premium',
    'direct',
    'horoscope_ai_chat',
    'influencias',
    'navbar',
    'navbar_mobile',
    'premium_required',
    'settings_subscription',
    'settings_upgrade'
]);

function getSafePageContext() {
    const pathname = window.location.pathname || '/';
    const privateResultRoute = pathname.match(PRIVATE_RESULT_PATH);

    if (privateResultRoute?.[1] === 'mapa') {
        return {
            pageUrl: BIRTHCHART_ANALYTICS_URL,
            pageTitle: BIRTHCHART_ANALYTICS_TITLE
        };
    }

    if (privateResultRoute?.[1] === 'horoscopo') {
        return {
            pageUrl: HOROSCOPE_ANALYTICS_URL,
            pageTitle: HOROSCOPE_ANALYTICS_TITLE
        };
    }

    return {
        pageUrl: pathname,
        pageTitle: document.title
    };
}

function sanitizeSubscriptionSource(source) {
    if (typeof source !== 'string' || source.trim() === '') {
        return 'direct';
    }

    const normalizedSource = source.trim().toLowerCase();

    return SUBSCRIPTION_SOURCE_ALLOWLIST.has(normalizedSource) ? normalizedSource : 'other';
}

class Analytics {
    constructor() {
        this.isGtagLoaded = false;
        this.eventQueue = [];
        this.checkGtagLoaded();
    }

    checkGtagLoaded() {
        if (typeof gtag !== 'undefined') {
            this.isGtagLoaded = true;
            this.processQueue();
        } else {
            setTimeout(() => this.checkGtagLoaded(), 100);
        }
    }

    processQueue() {
        while (this.eventQueue.length > 0) {
            const event = this.eventQueue.shift();
            this.trackEvent(event.name, event.parameters);
        }
    }

    trackEvent(eventName, parameters = {}) {
        if (!this.isGtagLoaded) {
            this.eventQueue.push({ name: eventName, parameters });
            return;
        }

        const safePageContext = getSafePageContext();
        const defaultParams = {
            timestamp: new Date().toISOString(),
            page_url: safePageContext.pageUrl,
            page_title: safePageContext.pageTitle
        };

        const finalParams = { ...defaultParams, ...parameters };

        // Never let a caller restore private URL/title context, referrers, or chart identifiers.
        Object.keys(finalParams).forEach(key => {
            const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (PRIVATE_ANALYTICS_PARAMETER_KEYS.has(normalizedKey)) {
                delete finalParams[key];
            }

            if (RESERVED_ACQUISITION_PARAMETER_KEYS.has(key.toLowerCase())) {
                delete finalParams[key];
            }
        });
        finalParams.page_url = safePageContext.pageUrl;
        finalParams.page_title = safePageContext.pageTitle;
        
        try {
            gtag('event', eventName, finalParams);
        } catch (error) {
            // Failed to track analytics event
        }
    }

    // Get user authentication status
    getUserStatus() {
        let isLoggedIn;
        const serverAuthIndicator = document.querySelector('[data-user-authenticated]');
        const serverAuthValue = serverAuthIndicator?.getAttribute('data-user-authenticated');

        // Authenticated application pages expose the server-authoritative state.
        if (typeof window.isLoggedIn === 'boolean') {
            isLoggedIn = window.isLoggedIn;
        } else if (serverAuthValue === 'true' || serverAuthValue === 'false') {
            isLoggedIn = serverAuthValue === 'true';
        } else {
            // Legacy pages fall back to their existing DOM and storage indicators.
            isLoggedIn = document.body.classList.contains('logged-in') ||
                         document.querySelector('[data-user-logged-in]') !== null;

            if (!isLoggedIn) {
                try {
                    isLoggedIn = localStorage.getItem('user_logged_in') === 'true';
                } catch (e) {
                    // localStorage is blocked - silently fallback to DOM checks
                    // This is expected behavior for privacy-focused users
                    console.debug('localStorage access denied - using DOM fallback');
                }
            }
        }
        
        return {
            is_logged_in: isLoggedIn,
            user_type: isLoggedIn ? 'authenticated' : 'anonymous'
        };
    }

    // AI interaction events
    trackAIButtonClick(context, hasRequiredAuth = false) {
        this.trackEvent('ai_button_click', {
            context: context, // 'birthchart', 'horoscope'
            required_auth: hasRequiredAuth,
            ...this.getUserStatus()
        });
    }

    trackAIChatOpen(context) {
        this.trackEvent('ai_chat_open', {
            context: context,
            ...this.getUserStatus()
        });
    }

    trackAIQuestionSubmit(context, questionLength) {
        this.trackEvent('ai_question_submit', {
            context: context,
            question_length: questionLength,
            ...this.getUserStatus()
        });
    }

    trackAIResponseReceived(context, responseTime) {
        this.trackEvent('ai_response_received', {
            context: context,
            response_time_ms: responseTime,
            ...this.getUserStatus()
        });
    }

    // Enhanced AI interaction events
    trackAIChatClose(context, sessionDuration = null, messageCount = 0) {
        this.trackEvent('ai_chat_close', {
            context: context,
            session_duration_ms: sessionDuration,
            message_count: messageCount,
            ...this.getUserStatus()
        });
    }

    trackAIInputFocus(context) {
        this.trackEvent('ai_input_focus', {
            context: context,
            ...this.getUserStatus()
        });
    }

    trackAIInputTyping(context, hasContent = false) {
        this.trackEvent('ai_input_typing', {
            context: context,
            has_content: hasContent,
            ...this.getUserStatus()
        });
    }

    trackAIMessageError(context, errorType) {
        this.trackEvent('ai_message_error', {
            context: context,
            error_type: errorType, // 'network', 'cooldown', 'auth', 'server'
            ...this.getUserStatus()
        });
    }

    trackAICooldownHit(context, remainingTimeMs = null, isModalShown = false) {
        this.trackEvent('ai_cooldown_hit', {
            context: context,
            remaining_time_ms: remainingTimeMs,
            modal_shown: isModalShown,
            ...this.getUserStatus(),
            subscription_tier: 'free' // Only free users hit cooldown
        });
    }

    trackAIPremiumUpsell(context, trigger) {
        this.trackEvent('ai_premium_upsell', {
            context: context,
            trigger: trigger, // 'cooldown', 'limit_reached', 'feature_gate'
            ...this.getUserStatus()
        });
    }

    trackAIChatHistoryLoad(context, messageCount = 0) {
        this.trackEvent('ai_chat_history_load', {
            context: context,
            message_count: messageCount,
            ...this.getUserStatus()
        });
    }

    trackAIQuestionSuggestionClick(context, suggestion) {
        this.trackEvent('ai_question_suggestion_click', {
            context: context,
            suggestion_length: typeof suggestion === 'string' ? suggestion.length : 0,
            ...this.getUserStatus()
        });
    }

    trackAIEngagement(context, engagementType, value = null) {
        const eventParams = {
            context: context,
            engagement_type: engagementType, // 'session_start', 'message_sent', 'response_read', 'session_end'
            ...this.getUserStatus()
        };

        if (value !== null) {
            eventParams.value = value;
        }

        this.trackEvent('ai_engagement', eventParams);
    }

    // Cooldown-specific funnel tracking
    trackCooldownFunnel(step, context, additionalParams = {}) {
        this.trackEvent('ai_cooldown_funnel', {
            step: step, // 'hit', 'modal_shown', 'subscribe_click', 'modal_close', 'conversion'
            context: context,
            ...additionalParams,
            ...this.getUserStatus()
        });
    }

    // Authentication funnel events
    trackAuthPrompt(trigger, context) {
        this.trackEvent('auth_prompt_shown', {
            trigger: trigger, // 'ai_access', 'premium_feature', 'save_chart'
            context: context,
            ...this.getUserStatus()
        });
    }

    trackSignupStart(source) {
        this.trackEvent('signup_start', {
            auth_source: source,
            ...this.getUserStatus()
        });
    }

    trackSignupComplete(source) {
        this.trackEvent('signup_complete', {
            auth_source: source,
            ...this.getUserStatus()
        });
    }

    trackLoginStart(source) {
        this.trackEvent('login_start', {
            auth_source: source,
            ...this.getUserStatus()
        });
    }

    trackLoginComplete(source) {
        this.trackEvent('login_complete', {
            auth_source: source,
            user_type: 'authenticated'
        });
    }

    // Drop-off tracking
    trackUserDropOff(context, step) {
        this.trackEvent('user_drop_off', {
            context: context,
            step: step,
            ...this.getUserStatus()
        });
    }

    // Page view events with context
    trackPageView(pageType, additionalParams = {}) {
        this.trackEvent('page_view', {
            page_type: pageType,
            ...additionalParams,
            ...this.getUserStatus()
        });
    }

    // Horoscope interaction events
    trackHoroscopeView(type) {
        this.trackEvent('horoscope_view', {
            horoscope_type: type, // 'daily', 'natal', 'transit'
            ...this.getUserStatus()
        });
    }

    trackHoroscopeExpansion(sectionName) {
        this.trackEvent('horoscope_section_expand', {
            section_name: sectionName,
            ...this.getUserStatus()
        });
    }

    // Birthchart accordion interaction events
    trackBirthchartAccordionOpen(accordionType, accordionName, accordionIndex) {
        this.trackEvent('birthchart_accordion_open', {
            accordion_type: accordionType, // 'house' or 'planet'
            accordion_name: accordionName, // e.g., 'Casa 1', 'Sol', 'Lua'
            accordion_index: accordionIndex, // numeric index
            page_url: BIRTHCHART_ANALYTICS_URL,
            ...this.getUserStatus()
        });
    }

    trackBirthchartAccordionClose(accordionType, accordionName, accordionIndex, timeOpenMs) {
        this.trackEvent('birthchart_accordion_close', {
            accordion_type: accordionType,
            accordion_name: accordionName,
            accordion_index: accordionIndex,
            time_open_ms: timeOpenMs,
            page_url: BIRTHCHART_ANALYTICS_URL,
            ...this.getUserStatus()
        });
    }

    trackBirthchartAccordionEngagement(totalOpened, uniqueOpened, accordionTypes) {
        this.trackEvent('birthchart_accordion_engagement', {
            total_opened: totalOpened, // total number of accordion opens (including re-opens)
            unique_opened: uniqueOpened, // number of unique accordions opened
            accordion_types: accordionTypes, // object with counts per type {houses: 3, planets: 2}
            page_url: BIRTHCHART_ANALYTICS_URL,
            ...this.getUserStatus()
        });
    }

    trackBirthchartPageView(hasBirthTime) {
        this.trackEvent('birthchart_page_view', {
            has_birth_time: hasBirthTime, // whether birth time is known
            page_url: BIRTHCHART_ANALYTICS_URL,
            ...this.getUserStatus()
        });
    }

    trackBirthchartFirstAccordionOpen() {
        this.trackEvent('birthchart_user_opened_accordion', {
            action: 'first_accordion_opened',
            page_url: BIRTHCHART_ANALYTICS_URL,
            ...this.getUserStatus()
        });
    }

    // Baseline tracking for Daily Influences feature
    trackBirthchartSession(isReturnVisit, daysSinceLastVisit = null) {
        this.trackEvent('birthchart_session', {
            is_return_visit: isReturnVisit,
            days_since_last_visit: daysSinceLastVisit,
            page_url: BIRTHCHART_ANALYTICS_URL,
            ...this.getUserStatus()
        });
    }

    trackBirthchartToHoroscope() {
        this.trackEvent('birthchart_to_horoscope_navigation', {
            source_page: BIRTHCHART_ANALYTICS_URL,
            ...this.getUserStatus()
        });
    }

    // Conversion events
    trackConversion(conversionType, value = null) {
        const eventParams = {
            conversion_type: conversionType,
            ...this.getUserStatus()
        };

        if (value !== null) {
            eventParams.value = value;
        }

        this.trackEvent('conversion', eventParams);
    }

    // Subscription tracking events
    trackSubscriptionPageView(source, abTestVariant = null) {
        this.trackEvent('subscription_page_view', {
            subscription_source: sanitizeSubscriptionSource(source),
            ab_test_variant: abTestVariant,
            ...this.getUserStatus()
        });
    }

    trackSubscriptionButtonClick(source, buttonLocation, priceVariant = null, abTestVariant = null) {
        this.trackEvent('subscription_button_click', {
            subscription_source: sanitizeSubscriptionSource(source),
            button_location: buttonLocation, // 'main', 'premium_card', 'modal'
            price_variant: priceVariant,
            ab_test_variant: abTestVariant,
            ...this.getUserStatus()
        });
    }

    trackSubscriptionCheckoutStart(source, priceVariant = null, abTestVariant = null) {
        this.trackEvent('subscription_checkout_start', {
            subscription_source: sanitizeSubscriptionSource(source),
            price_variant: priceVariant,
            ab_test_variant: abTestVariant,
            ...this.getUserStatus()
        });
    }

    trackSubscriptionCheckoutComplete(source, priceVariant = null, abTestVariant = null, sessionId = null) {
        this.trackEvent('subscription_checkout_complete', {
            subscription_source: sanitizeSubscriptionSource(source),
            price_variant: priceVariant,
            ab_test_variant: abTestVariant,
            session_id: sessionId,
            ...this.getUserStatus()
        });
    }

    trackSubscriptionConversion(source, priceVariant = null, abTestVariant = null, value = null) {
        this.trackEvent('subscription_conversion', {
            subscription_source: sanitizeSubscriptionSource(source),
            price_variant: priceVariant,
            ab_test_variant: abTestVariant,
            value: value,
            ...this.getUserStatus()
        });
    }

    trackSubscriptionFunnelStep(step, source, additionalParams = {}) {
        this.trackEvent('subscription_funnel', {
            funnel_step: step, // 'view', 'click', 'checkout_start', 'checkout_complete', 'success'
            ...additionalParams,
            subscription_source: sanitizeSubscriptionSource(source),
            ...this.getUserStatus()
        });
    }

    trackFeatureGate(feature, source, action) {
        this.trackEvent('feature_gate_hit', {
            feature: feature, // 'ai_chat', 'compatibility_full', 'horoscope_personalized', etc.
            feature_context: source, // 'birthchart', 'horoscope', 'compatibility', etc.
            action: action, // 'view_prompt', 'click_unlock', 'redirect_subscription'
            ...this.getUserStatus()
        });
    }

    // Helper to get source from URL parameter
    getSourceFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        return sanitizeSubscriptionSource(urlParams.get('source') || urlParams.get('utm_source'));
    }

    sanitizeSubscriptionSource(source) {
        return sanitizeSubscriptionSource(source);
    }

    // Helper to store source in session
    storeSubscriptionSource(source) {
        try {
            sessionStorage.setItem('subscription_source', sanitizeSubscriptionSource(source));
            sessionStorage.setItem('subscription_source_timestamp', Date.now().toString());
        } catch (e) {
            console.debug('sessionStorage access denied');
        }
    }

    // Helper to retrieve stored source
    getStoredSubscriptionSource() {
        try {
            const source = sessionStorage.getItem('subscription_source');
            const timestamp = sessionStorage.getItem('subscription_source_timestamp');
            
            // Check if source is still valid (within 30 minutes)
            if (source && timestamp) {
                const age = Date.now() - parseInt(timestamp);
                if (age < 30 * 60 * 1000) { // 30 minutes
                    return sanitizeSubscriptionSource(source);
                }
            }
        } catch (e) {
            console.debug('sessionStorage access denied');
        }
        return null;
    }
}

// Create global analytics instance
window.astrolooAnalytics = new Analytics();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = Analytics;
}
